import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faBolt,
  faTag,
  faBuilding,
  faEnvelope,
  faShieldHalved,
  faHelmetSafety,
  faFileInvoice,
  faBoxesStacked,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getProductPrimaryImage,
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  listStorefrontProducts,
  listStorefrontTestimonials,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'

const WA_NUMBER = '6285704011028'
const LP_WALL_COUNT = 24
const LP_WALL_IMAGE_TRANSFORM = { width: 380, format: 'webp', quality: 55 } as const

const PRODUCT_NAME = 'Sarung Tangan Proyek'
const HERO_IMAGE = '/lp/sarung-tangan-katalog.jpeg'

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// Testimonials API returns proof photos (chat/order screenshots) with no
// per-person text, so the "wall of proof" is built from the real images.
function extractTestimonialImages(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  const urls: string[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const media = (entry as Record<string, unknown>).media_items
    if (!Array.isArray(media)) continue
    for (const item of media) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const isImage = record.media_type === 'image' || record.media_type == null
      const url = typeof record.url === 'string' ? record.url : typeof record.storage_url === 'string' ? record.storage_url : null
      if (isImage && url) urls.push(url)
    }
  }
  return urls
}

// Project-buyer FAQ. This visitor buys per project (big, often urgent, sent to
// a site address), not on a monthly schedule, so it answers speed, delivery to
// site, and invoice-for-project-report questions.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Berapa harga sarung tangan proyek di Dropify?',
    a: 'Harga mengikuti jenis sarung tangan dan jumlah pesanan. Kirim jenis pekerjaan, produk, jumlah, serta lokasi proyek agar admin dapat menyiapkan quotation dan perkiraan ongkir.',
  },
  {
    q: 'Minimal ordernya berapa?',
    a: 'MOQ kategori sarung tangan umumnya mulai 25 lusin atau 300 pasang. Beberapa merek atau seri dapat memiliki ketentuan berbeda, jadi sebutkan produk yang dipilih saat meminta quotation.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Bisa kirim langsung ke lokasi proyek?',
    a: 'Bisa. Kirim alamat proyek atau gudang kontraktor serta jumlah barang. Admin akan membandingkan layanan ekspedisi dan mengirim nomor resi setelah barang diserahkan untuk pengiriman.',
  },
  {
    q: 'Butuh cepat, berapa lama sampai?',
    a: 'Kirim target penerimaan sejak awal. Admin akan memeriksa waktu penyiapan dan membandingkan layanan ekspedisi berdasarkan jumlah serta lokasi proyek.',
  },
  {
    q: 'Bisa minta invoice resmi buat laporan proyek?',
    a: 'Bisa. Kami kirim invoice resmi untuk kebutuhan administrasi dan laporan pengeluaran proyek. Pemesanan pakai PO juga kami layani.',
  },
  {
    q: 'Jenis apa yang sesuai untuk proyek bangunan?',
    a: 'Untuk proyek tersedia sarung tangan bintik PVC dan katun rajut Matahari untuk kerja umum, angkut, dan bongkar muat. Gekko tersedia dalam lini Nitrile dan PU. Untuk kebutuhan K3 yang lebih spesifik, kirim jenis pekerjaan dan standar wajib agar admin dapat memeriksa seri Gekko atau COMET yang sesuai.',
  },
  {
    q: 'Di mana beli sarung tangan proyek terdekat?',
    a: 'Dropify menerima pemesanan melalui WhatsApp dan mengirim dari Surabaya ke berbagai kota di Indonesia. Admin memberi informasi waktu penyiapan dan estimasi perjalanan sesuai lokasi proyek serta layanan ekspedisi.',
  },
  {
    q: 'Ada sarung tangan karet untuk tukang?',
    a: 'Gekko tersedia dalam lini Nitrile dan PU. Nitrile biasa dipertimbangkan untuk pekerjaan mekanikal dan permukaan yang berpotensi berminyak, sedangkan PU lebih sesuai untuk handling barang kering yang membutuhkan ketangkasan tangan. Kirim jenis pekerjaan Anda agar admin dapat membantu memeriksa serinya.',
  },
  {
    q: 'Bisa minta sample dulu?',
    a: 'Bisa. Biaya sample terdiri dari harga satuan dan ongkir. Mandor atau tim K3 dapat memeriksa bahan, ukuran, grip, marking, serta dokumen sebelum pengadaan besar.',
  },
  {
    q: 'Bisa nego kalau ambil buat beberapa proyek sekaligus?',
    a: 'Bisa. Kirim total kebutuhan, pembagian per proyek, lokasi, dan target penerimaan. Admin akan menyusun quotation serta pilihan jadwal pengiriman.',
  },
]

// Model-level catalog transcribed from Dropify's own product photos in
// /public/lp (the Gekko Grip shots and the knit-glove catalog sheet). These
// SKUs are not listed in the storefront API, so this LP carries the model
// catalog itself; prices per lusin follow volume and are quoted via WhatsApp.
const GEKKO_VARIANTS = [
  {
    image: '/lp/gekko-biru.jpeg',
    name: 'Gekko Grip Biru',
    desc: 'Liner rajut putih, telapak dilapis nitrile biru. Marking CE, EN388, dan EN420 tercetak di punggung tangan.',
  },
  {
    image: '/lp/gekko-hitam.jpeg',
    name: 'Gekko Grip Hitam',
    desc: 'Liner polyester hitam dengan lapisan nitrile hitam pada telapak. Ditujukan untuk pekerjaan mekanikal sesuai batas penggunaan serinya.',
  },
  {
    image: '/lp/gekko-putih.jpeg',
    name: 'Gekko Grip Putih Hitam',
    desc: 'Liner putih dengan lapisan nitrile hitam pada telapak. Tim K3 perlu memeriksa marking CE, EN388, EN420, dan dokumen seri sebelum memilih.',
  },
]

const KNIT_MODELS = [
  { kode: 'T-712 N', varian: 'Gelang Putih, 7 benang', berat: '±900 gr' },
  { kode: 'T-612 N', varian: 'Gelang Merah, 6 benang', berat: '±700 gr' },
  { kode: 'T-512 N', varian: 'Gelang Biru, 5 benang', berat: '±600 gr' },
  { kode: 'T-412 N', varian: 'Gelang Kuning, 4 benang', berat: '±500 gr' },
  { kode: 'T-312 N', varian: 'Gelang Hijau, 3 benang', berat: '±400 gr' },
  { kode: 'T-650 N', varian: 'Abu Gelang Merah', berat: '±650 gr' },
  { kode: 'T-550 N', varian: 'Abu Gelang Biru', berat: '±550 gr' },
  { kode: 'T-450 N', varian: 'Abu Gelang Kuning', berat: '±450 gr' },
  { kode: 'T-500 OR', varian: 'Putih', berat: '±500 gr' },
  { kode: 'T-500 OR', varian: 'Putih Bintik Orange', berat: '±500 gr' },
  { kode: 'T-500 OR', varian: 'Abu Bintik Hitam', berat: '±500 gr' },
  { kode: 'T-580 N', varian: 'Putih Bintik Hitam', berat: '±580 gr' },
  { kode: 'T-580 N', varian: 'Putih Bintik Kuning', berat: '±580 gr' },
  { kode: 'T-450 N', varian: 'Putih Bintik Hitam', berat: '±500 gr' },
]

// Concrete, extractable facts for search and AI answers. Same claims as the
// pabrik page's table, with project-flavored use cases.
const SPEC_ROWS = [
  {
    seri: 'Sarung Tangan Bintik PVC',
    bahan: 'Katun rajut, bintik PVC anti-slip',
    standar: '-',
    cocok: 'Bangunan, angkut material, bongkar muat',
  },
  {
    seri: 'Sarung Tangan Katun Rajut',
    bahan: 'Katun rajut 3–7 benang',
    standar: '-',
    cocok: 'Kerja umum harian di lapangan',
  },
  {
    seri: 'Gekko Grip Nitrile',
    bahan: 'Rajut dengan lapisan nitrile pada telapak dan jari',
    standar: 'Marking dan dokumen dikonfirmasi per seri',
    cocok: 'Mekanikal dan komponen yang berpotensi terkena minyak ringan',
  },
  {
    seri: 'Gekko PU',
    bahan: 'Rajut dengan lapisan PU pada telapak dan jari',
    standar: 'Konfirmasi per seri',
    cocok: 'Perakitan, pengepakan, handling barang kering',
  },
  {
    seri: 'Sarung Tangan Matahari',
    bahan: 'Katun rajut, polos & warna warni',
    standar: '-',
    cocok: 'Kerja umum dan pemindahan material kering',
  },
  {
    seri: 'COMET CG Series',
    bahan: 'Liner rajut lapis PU, PVC, & nitrile; ada cut resistant, ESD, impact, chemical',
    standar: 'Seri K3 industri',
    cocok: 'Fabrikasi, K3 ketat, area kimia & elektronik',
  },
]

// Compact card model for the live brand strips; mapped in the loader so the
// SSR payload carries only what the cards render.
type BrandProductCard = { name: string; slug: string; image: string }

function toBrandCards(products: StorefrontProduct[]): BrandProductCard[] {
  const seen = new Set<string>()
  const cards: BrandProductCard[] = []
  for (const product of products) {
    // Catalog holds duplicate Matahari entries under -kk-katalog slugs.
    if (!product.name.toLowerCase().startsWith('sarung tangan') || seen.has(product.name)) continue
    seen.add(product.name)
    cards.push({
      name: product.name,
      slug: product.slug,
      image: getProductPrimaryImage(product, { width: 380, format: 'webp', quality: 70 }),
    })
  }
  return cards
}

export const Route = createFileRoute('/sarung-tangan-proyek')({
  loader: async () => {
    // Config failure falls back to null: the contact helpers carry hardcoded
    // defaults, and paid traffic must never land on a 500. Brand strips fetch
    // live so the section stays in sync with the dashboard catalog.
    const [config, testimonialsRaw, cometRaw, matahariRaw, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
      listStorefrontProducts({ search: 'comet', per_page: 30 }).catch(() => null),
      listStorefrontProducts({ search: 'matahari', per_page: 10 }).catch(() => null),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
      cometProducts: toBrandCards(cometRaw?.items ?? []),
      matahariProducts: toBrandCards(matahariRaw?.items ?? []),
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Sarung Tangan Proyek & Kontraktor Partai Besar')
    const description =
      'Grosir sarung tangan proyek untuk kontraktor: Gekko Grip, Matahari, dan COMET CG. Kirim kebutuhan kerja, jumlah, serta kota tujuan untuk meminta quotation.'
    const ogImage = absoluteUrl(HERO_IMAGE)

    return {
      // Indexable: targets "sarung tangan proyek/kontraktor" searches and
      // doubles as a paid-ads destination for project-intent keywords.
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/sarung-tangan-proyek') },
        { property: 'og:image', content: ogImage || DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage || DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/sarung-tangan-proyek') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Sarung Tangan Proyek Dropify',
            itemListElement: [
              ...GEKKO_VARIANTS.map((v, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@type': 'Thing',
                  name: v.name,
                  description: v.desc,
                  image: absoluteUrl(v.image),
                  brand: { '@type': 'Brand', name: 'Gekko Grip' },
                },
              })),
              {
                '@type': 'ListItem',
                position: GEKKO_VARIANTS.length + 1,
                item: {
                  '@type': 'Thing',
                  name: 'Sarung Tangan Katun Rajut T-312 N sampai T-712 N',
                  description: 'Sarung tangan katun rajut 3 sampai 7 benang, berat ±400 sampai ±900 gr per lusin.',
                  image: absoluteUrl('/lp/sarung-tangan-rajut-t712.jpeg'),
                },
              },
              {
                '@type': 'ListItem',
                position: GEKKO_VARIANTS.length + 2,
                item: {
                  '@type': 'Thing',
                  name: 'Sarung Tangan Bintik PVC T-500 OR & T-580 N',
                  description: 'Sarung tangan rajut dengan bintik PVC anti-slip di telapak, berat ±500 sampai ±580 gr per lusin.',
                  image: absoluteUrl('/lp/sarung-tangan-bintik-t580.jpeg'),
                },
              },
              {
                '@type': 'ListItem',
                position: GEKKO_VARIANTS.length + 3,
                item: {
                  '@type': 'Thing',
                  name: 'Sarung Tangan Matahari',
                  description: 'Sarung tangan katun rajut Matahari dalam varian polos dan warna-warni untuk kerja umum.',
                  brand: { '@type': 'Brand', name: 'Matahari' },
                },
              },
              {
                '@type': 'ListItem',
                position: GEKKO_VARIANTS.length + 4,
                item: {
                  '@type': 'Thing',
                  name: 'Sarung Tangan COMET CG Series',
                  description: 'Sarung tangan kerja industri COMET seri CG: PU, PVC, nitrile, cut resistant, ESD, impact, dan chemical nitrile.',
                  brand: { '@type': 'Brand', name: 'COMET' },
                },
              },
            ],
          }),
        },
      ],
    }
  },
  component: ProjectLandingPage,
})

// Amber highlighter swipe behind the hook keyword — the page's signature.
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation sesuai kebutuhan',
    desc: 'Quotation merinci produk, jumlah, harga, dan perkiraan ongkir untuk lokasi proyek.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim ke lokasi proyek',
    desc: 'Pesanan dapat dikirim ke alamat proyek atau gudang kontraktor. Admin mengirim nomor resi setelah barang diserahkan kepada ekspedisi.',
  },
  {
    icon: faBoxesStacked,
    title: 'Pengecekan volume proyek',
    desc: 'Kirim produk dan jumlah yang dibutuhkan. Admin akan memeriksa ketersediaan dan waktu penyiapan sebelum PO.',
  },
  {
    icon: faFileInvoice,
    title: 'Invoice Resmi & Bisa PO',
    desc: 'Invoice resmi untuk laporan pengeluaran proyek, pemesanan pakai PO juga dilayani.',
  },
]

const FIT_ITEMS = [
  'Kontraktor atau subkon yang butuh sarung tangan kerja partai besar untuk pekerja di lokasi proyek.',
  'Mandor atau site manager yang mengelola APD harian pekerja di lapangan.',
  'Tim pengadaan proyek konstruksi, jalan, atau infrastruktur yang membutuhkan quotation dan invoice.',
  'Workshop, bengkel las, atau fabrikasi yang memakai sarung tangan secara rutin.',
  'Proyek yang memiliki target penerimaan dan memerlukan estimasi penyiapan serta pengiriman.',
  'Kontraktor yang ingin membandingkan biaya berdasarkan produk dan volume pesanan.',
]

const PRODUCT_TYPES = [
  {
    image: '/lp/sarung-tangan-bintik-t580.jpeg',
    title: 'Sarung Tangan Bintik PVC',
    desc: 'Sarung tangan rajut berbintik PVC untuk membantu cengkeraman saat mengangkat material kering. Model yang ditampilkan mencakup T-500 OR dan T-580 N.',
    tag: 'Bintik PVC',
  },
  {
    image: '/lp/sarung-tangan-gekko-spread.jpeg',
    title: 'Gekko Grip Nitrile',
    desc: 'Telapak berlapis nitrile untuk pekerjaan mekanikal dan penanganan komponen yang berpotensi terkena minyak ringan.',
    tag: 'Heavy duty',
  },
  {
    image: '/lp/sarung-tangan-gekko-3.jpeg',
    title: 'Gekko Grip PU',
    desc: 'Lapisan PU untuk penanganan barang kering dan pekerjaan yang membutuhkan ketangkasan tangan. Marking serta dokumen perlu diperiksa per seri sebelum PO.',
    tag: 'Lapisan PU',
  },
  {
    image: '/lp/sarung-tangan-rajut-t712.jpeg',
    title: 'Sarung Tangan Katun Rajut',
    desc: 'Tersedia dalam konstruksi 3–7 benang, dari kode T-312 N sampai T-712 N. Pilih ketebalan berdasarkan pekerjaan dan benda yang ditangani.',
    tag: 'Ekonomis',
  },
]

const OBJECTIONS = [
  {
    worry: '“Proyek sudah berjalan dan barang dibutuhkan segera.”',
    answer: 'Sampaikan target penerimaan sejak awal. Admin akan memeriksa ketersediaan, waktu penyiapan, dan pilihan ekspedisi untuk lokasi proyek Anda.',
  },
  {
    worry: '“Lokasi proyek berada di luar kota.”',
    answer: 'Kirim alamat proyek dan jumlah barang. Admin akan membandingkan layanan ekspedisi dan mencantumkan perkiraan ongkir pada quotation.',
  },
  {
    worry: '“Kami membutuhkan invoice untuk laporan proyek.”',
    answer: 'Sampaikan kebutuhan dokumen sejak awal. Dropify dapat memproses PO dan menyiapkan invoice untuk pesanan.',
  },
  {
    worry: '“Kami perlu membandingkan penawaran pemasok.”',
    answer: 'Minta quotation dengan produk, jumlah, dan lokasi yang sama agar harga serta ongkir dapat dibandingkan secara setara.',
  },
]

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PRODUCT_NAME,
          ...(itemName ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PRODUCT_NAME }] } : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function ProjectLandingPage() {
  const { config, testimonialImages, cometProducts, matahariProducts, newsPosts } = Route.useLoaderData()

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  const heroMessage = 'Halo Dropify, saya membutuhkan sarung tangan kerja untuk proyek ___. Perkiraan jumlah ___ lusin, kirim ke ___. Mohon siapkan quotation dan estimasi ongkir.'
  const softMessage = 'Halo Dropify, saya sedang membandingkan sarung tangan untuk pekerjaan ___. Mohon jelaskan seri dan data keselamatan yang perlu diperiksa.'
  const ctaMessage = 'Halo Dropify, saya ingin meminta quotation sarung tangan proyek. Kebutuhan ___ pasang untuk pekerjaan ___, kirim ke ___. Mohon sertakan estimasi ongkir.'

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Kirim jenis pekerjaan, jumlah, dan lokasi proyek untuk meminta quotation.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta quotation"
            placement="header"
            className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
          />
        </div>
      </header>

      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              <FontAwesomeIcon icon={faHelmetSafety} className="h-3 w-3" />
              Untuk kontraktor, proyek, & workshop
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Sarung tangan proyek partai besar, <Mark>dikirim ke lokasi proyek.</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Dropify memasok sarung tangan kerja untuk kontraktor dan pengadaan proyek. Kirim jenis pekerjaan, standar keselamatan, jumlah, dan kota tujuan agar admin dapat memeriksa seri serta menyiapkan quotation. Kebutuhan PO dan invoice dapat disampaikan sejak awal.
            </p>

            <div className="mt-6 inline-flex items-baseline gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Harga dan stok</span>
              <span className="text-lg font-black leading-none text-white">Dikonfirmasi melalui quotation</span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta quotation dan ongkir"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href={waLink(softMessage)}
                {...whatsAppCtaProps('landing_page', { cta_placement: 'hero_secondary', lp_category: PRODUCT_NAME })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Bandingkan seri terlebih dahulu →
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Quotation memuat produk, jumlah, harga, dan ongkir ke lokasi proyek Anda.</p>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl bg-white">
              <img src={HERO_IMAGE} alt="Sarung tangan proyek partai besar dari Dropify" width="800" height="800" loading="eager" fetchPriority="high" decoding="async" className="w-full aspect-square object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-3 sm:left-4 flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400">
                <FontAwesomeIcon icon={faTruckFast} className="h-4 w-4 text-slate-900" />
              </span>
              <div className="leading-tight">
                <p className="text-xs font-black uppercase tracking-wide text-slate-900">Kirim ke Site</p>
                <p className="text-[11px] text-slate-500">Langsung ke lokasi proyek</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AdNewsProofSection posts={newsPosts} />

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* Who it's for */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Kebutuhan proyek yang kami layani</h2>
            <p className="mt-2 text-base text-slate-500">Bagian ini membantu kontraktor dan tim pengadaan mengenali jenis kebutuhan yang dapat dibahas dengan admin.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Kenapa kontraktor ambil sarung tangan di sini?</h2>
            <p className="mt-2 text-base text-slate-500">Empat alasan yang bikin pengadaan proyek pilih Dropify.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHY_US_ITEMS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Cara meminta quotation untuk proyek</h2>
            <p className="mt-2 text-base text-slate-500">Tiga langkah, dari chat sampai barang sampai di lokasi.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { n: '1', title: 'Kirim kebutuhan dan lokasi', desc: 'Sebutkan jenis pekerjaan, jumlah pasang, dan alamat lokasi proyek.' },
              { n: '2', title: 'Periksa quotation', desc: 'Admin mengirim pilihan produk dan quotation. Ongkir dicantumkan setelah layanan ekspedisi ditentukan.' },
              { n: '3', title: 'Barang dikirim ke lokasi', desc: 'Setelah pembayaran dikonfirmasi, admin memberi informasi waktu penyiapan. Resi dikirim setelah paket diserahkan kepada ekspedisi.' },
            ].map((step) => (
              <div key={step.n} className="rounded-2xl border border-slate-100 bg-white p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-sm font-black text-slate-900">{step.n}</span>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product types showcase */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Jenis sarung tangan buat proyek</h2>
            <p className="mt-2 text-base text-slate-500">Bandingkan sarung tangan berbintik PVC dengan seri Gekko Nitrile atau PU berdasarkan permukaan benda, risiko kerja, dan standar proyek.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PRODUCT_TYPES.map((type) => (
              <div key={type.title} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg sm:flex-row">
                <div className="relative overflow-hidden bg-white sm:w-44 sm:flex-shrink-0">
                  <img src={type.image} alt={type.title} width="400" height="400" loading="lazy" decoding="async" className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" />
                  <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">{type.tag}</span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-base font-black tracking-tight text-slate-900">{type.title}</h3>
                  <p className="mt-1.5 flex-1 text-base leading-relaxed text-slate-500">{type.desc}</p>
                  <WhatsAppButton
                    message={`Halo Dropify, proyek kami butuh ${type.title}. Boleh minta quotation sama ongkir ke lokasi kami?`}
                    label="Periksa stok dan quotation"
                    itemName={type.title}
                    className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Quick-compare spec table */}
          <div className="mt-10">
            <h3 className="text-lg font-black tracking-tight text-slate-900">Perbandingan cepat jenis sarung tangan proyek</h3>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-bold">Seri / Jenis</th>
                    <th className="px-4 py-3 font-bold">Bahan</th>
                    <th className="px-4 py-3 font-bold">Standar</th>
                    <th className="px-4 py-3 font-bold">Cocok untuk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {SPEC_ROWS.map((row) => (
                    <tr key={row.seri}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.seri}</td>
                      <td className="px-4 py-3 text-slate-600">{row.bahan}</td>
                      <td className="px-4 py-3 text-slate-600">{row.standar}</td>
                      <td className="px-4 py-3 text-slate-600">{row.cocok}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Satu lusin berisi 12 pasang. MOQ sarung tangan umumnya mulai 25 lusin. Harga dan ketentuan per seri dicantumkan pada quotation.
            </p>
          </div>

          {/* Model catalog: real SKUs with photos, so the page shows exactly
              what's ready instead of claims. Data transcribed from Dropify's
              own product shots and catalog sheet. */}
          <div className="mt-12">
            <h3 className="text-lg font-black tracking-tight text-slate-900">Katalog model sarung tangan kerja</h3>
            <p className="mt-2 text-base text-slate-500">Bandingkan bahan, lapisan, ukuran, dan batas penggunaan. Sebutkan kode model saat menghubungi admin.</p>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {GEKKO_VARIANTS.map((v) => (
                <div key={v.name} className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
                  <img src={v.image} alt={`Sarung tangan ${v.name}`} width="342" height="645" loading="lazy" decoding="async" className="h-64 w-full bg-white object-contain p-2" />
                  <div className="border-t border-slate-100 p-4">
                    <h4 className="text-sm font-black tracking-tight text-slate-900">{v.name}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-bold">Kode</th>
                    <th className="px-4 py-3 font-bold">Varian</th>
                    <th className="px-4 py-3 font-bold">Berat per lusin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {KNIT_MODELS.map((m) => (
                    <tr key={`${m.kode}-${m.varian}`}>
                      <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-900">{m.kode}</td>
                      <td className="px-4 py-3 text-slate-600">{m.varian}</td>
                      <td className="px-4 py-3 text-slate-600">{m.berat}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Gelang itu warna list di pergelangan, penanda ketebalan benang. Bintik itu titik PVC anti-slip di telapak. Harga per lusin ngikutin volume, minta quotation buat angka pastinya.
            </p>
            <WhatsAppButton
              message={'Halo Dropify, saya mau tanya harga sarung tangan untuk proyek. Kode modelnya: ___. Kebutuhan sekitar ___ lusin.'}
              label="Minta quotation model ini"
              itemName="Katalog model"
              placement="model_catalog"
              className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
            />
          </div>

          {/* Live brand strips: Matahari & COMET straight from the catalog,
              so the list stays in sync with what's actually sold. */}
          {(matahariProducts.length > 0 || cometProducts.length > 0) && (
            <div className="mt-12">
              <h3 className="text-lg font-black tracking-tight text-slate-900">Pilihan lain: Matahari dan COMET</h3>
              <p className="mt-2 max-w-3xl text-base text-slate-500">
                Matahari menyediakan sarung tangan rajut untuk kerja umum. Seri COMET CG mencakup konstruksi PU, PVC, nitrile, cut resistant, ESD, impact, dan chemical nitrile. Periksa standar serta dokumen per seri.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[...matahariProducts, ...cometProducts.slice(0, 6)].map((product) => (
                  <a
                    key={product.slug}
                    href={`/produk/${product.slug}`}
                    onClick={() =>
                      track('click_internal_link', {
                        link_url: `/produk/${product.slug}`,
                        link_text: product.name,
                        link_location: 'proyek_brand_strip',
                      })
                    }
                    className="group overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-md"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      width="380"
                      height="380"
                      loading="lazy"
                      decoding="async"
                      className="aspect-square w-full bg-white object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <p className="p-3 text-sm font-semibold leading-snug text-slate-800 group-hover:text-blue-700">{product.name}</p>
                  </a>
                ))}
              </div>
              {cometProducts.length > 6 && (
                <p className="mt-3 text-sm text-slate-500">
                  Terdapat {cometProducts.length - 6} model COMET CG lain, termasuk cut resistant, aramid, impact, dan chemical nitrile. Kirim jenis pekerjaan agar admin dapat menunjukkan daftar yang relevan.
                </p>
              )}
              <WhatsAppButton
                message={'Halo Dropify, proyek kami butuh sarung tangan Matahari / COMET. Boleh minta daftar model, harga, dan ongkir ke lokasi kami?'}
                label="Minta quotation Matahari dan COMET"
                itemName="Matahari & COMET"
                placement="brand_catalog"
                className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
              />
            </div>
          )}

          {/* Trust badges */}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { icon: faShieldHalved, text: 'Gekko: pilihan Nitrile dan PU, dokumen diperiksa per seri' },
              { icon: faTruckFast, text: 'Kirim langsung ke alamat proyek atau gudang, se-Indonesia' },
              { icon: faBoxesStacked, text: 'Ketersediaan dan waktu penyiapan dikonfirmasi sebelum pembayaran' },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <FontAwesomeIcon icon={b.icon} className="h-4 w-4" />
                </span>
                <p className="text-sm font-medium leading-snug text-slate-600">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scalev-style wall of proof */}
      <TestimonialWall images={testimonialImages} />

      {/* Objection handling */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan sebelum pengadaan proyek</h2>
            <p className="mt-2 text-base text-slate-500">Jawaban berikut membahas harga, MOQ, sample, dokumen, dan pengiriman proyek.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIONS.map((item) => (
              <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-base font-bold text-slate-900">{item.worry}</p>
                <div className="mt-2 flex gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
            <p className="text-base font-semibold text-slate-700">Kirim persyaratan proyek kepada admin sebelum menerbitkan PO.</p>
            <WhatsAppButton
              message={softMessage}
              label="Tanyakan persyaratan proyek"
              placement="objection_section"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* SEO prose — works the project search terms in naturally, plus
          cross-links that keep this page from cannibalizing the pabrik and
          grosir pages. */}
      <section className="bg-white py-14 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Grosir sarung tangan proyek untuk kontraktor</h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              Dropify menyuplai <strong className="font-semibold text-slate-800">sarung tangan proyek</strong> untuk kontraktor, subkontraktor, dan tim pengadaan. Quotation disusun berdasarkan seri, jumlah, kota tujuan, serta kebutuhan dokumen yang disampaikan pembeli.
            </p>
            <p>
              Pilihan untuk proyek mencakup <strong className="font-semibold text-slate-800">sarung tangan bintik PVC</strong> untuk angkut material dan bongkar muat, <strong className="font-semibold text-slate-800">sarung tangan Matahari</strong> untuk kerja umum harian, serta <strong className="font-semibold text-slate-800">Gekko Nitrile dan PU</strong>. Seri COMET CG tersedia untuk kebutuhan K3 yang lebih spesifik. Kirim jenis pekerjaan, standar wajib, jumlah, dan lokasi proyek agar admin dapat memeriksa pilihan produk serta dokumennya.
            </p>
            <p>
              Jika Anda mencari sarung tangan kerja berlapis, Gekko tersedia dalam pilihan Nitrile dan PU. Jelaskan benda yang dipegang, kondisi area kerja, serta standar keselamatan proyek agar admin dapat membantu memeriksa seri yang sesuai.
            </p>
            <p>
              Halaman ini khusus untuk kebutuhan proyek dan kontraktor. Jika Anda menangani pengadaan pabrik dengan kebutuhan rutin, buka halaman{' '}
              <a href="/sarung-tangan-pabrik" className="font-semibold text-blue-600 hover:text-blue-700">
                supplier sarung tangan pabrik
              </a>{' '}
              kami. Jika Anda membeli sarung tangan untuk dijual kembali, buka halaman{' '}
              <a href="/lp/grosir-sarung-tangan" className="font-semibold text-blue-600 hover:text-blue-700">
                grosir sarung tangan
              </a>{' '}
              untuk melihat pilihan bagi reseller.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Minta quotation sarung tangan untuk proyek</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Sebutkan jenis pekerjaan, jumlah pasang, standar wajib, dan lokasi proyek. Admin akan menyiapkan quotation serta perkiraan ongkir.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={ctaMessage}
              label="Minta quotation via WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Konsultasi kebutuhan gratis, tanpa komitmen beli.
          </p>

          {/* Legitimacy: accepted payment methods */}
          <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/sarung-tangan-proyek" />

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pengadaan grosir melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: PRODUCT_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-400">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-400" />
        </div>
      </footer>
    </div>
  )
}

function TestimonialWall({ images }: { images: string[] }) {
  if (images.length === 0) return null

  const columns: string[][] = [[], [], []]
  images.forEach((src, i) => columns[i % 3].push(src))
  const durations = ['38s', '48s', '42s']
  const directions = ['lp-up', 'lp-down', 'lp-up']

  return (
    <section className="relative overflow-hidden bg-slate-50 py-14">
      <style>{`
        @keyframes lp-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes lp-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .lp-track { animation: lp-up var(--dur,40s) linear infinite; }
        .lp-track.lp-down { animation-name: lp-down; }
        .lp-wall:hover .lp-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .lp-track { animation: none; } }
      `}</style>

      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Dokumentasi layanan Dropify
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dokumentasi pesanan partai besar yang telah kami tangani</h2>
          <p className="mt-2 text-base text-slate-500">Dokumentasi ini berasal dari pemesanan, penyiapan barang, dan pengiriman beberapa kategori Dropify.</p>
        </div>
      </div>

      <div className="lp-wall relative mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {columns.map((column, ci) => (
            <div key={ci} className={`h-[460px] sm:h-[560px] overflow-hidden ${ci === 2 ? 'hidden lg:block' : ''}`}>
              <div className={`flex flex-col gap-4 lp-track ${directions[ci] === 'lp-down' ? 'lp-down' : ''}`} style={{ '--dur': durations[ci] } as CSSProperties}>
                {[...column, ...column].map((src, i) => (
                  <WallCard key={`${ci}-${i}`} src={toExternalImage(src, LP_WALL_IMAGE_TRANSFORM)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
    </section>
  )
}

function WallCard({ src }: { src: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <img src={src} alt="Dokumentasi pemesanan dan pengiriman Dropify" width="380" height="507" loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover" />
    </figure>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar sarung tangan proyek</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'proyek' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
